import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { formatCurrency } from '../../../shared/utils/formatCurrency';
import { formatDate } from '../../../shared/utils/formatDate';
import ReportProblemButton from '../../support/components/ReportProblemButton';
import { getLiveRound, hasCollectedThisCycle, collectedTimestamp, isCycleComplete, getScheduledCollectionDate, formatScheduleDate, getCollectionPosition } from '../../cycle-engine';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../../shared/utils/featureFlags';

const RotationView = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [hands, setHands] = useState([]);
  const [membersData, setMembersData] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPositions, setEditingPositions] = useState({});
  const [susuOn, setSusuOn] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch group data
        const { data: group } = await supabaseClient
          .from('groups')
          .select('*')
          .eq('id', parseInt(groupId, 10))
          .single();
        setGroupData(group);

        // Fetch hands
        const { data: handsData } = await supabaseClient
          .from('hands')
          .select('*')
          .eq('group_id', parseInt(groupId, 10))
          .order('position_number', { ascending: true });
        setHands(handsData || []);

        // Fetch members for reference
        const { data: membersResult } = await supabaseClient
          .from('members')
          .select('id, name, email')
          .eq('group_id', parseInt(groupId, 10));
        setMembersData(membersResult || []);

        // Fetch payouts to check collection status
        const { data: payoutsData } = await supabaseClient
          .from('payouts')
          .select('*')
          .eq('group_id', parseInt(groupId, 10));
        setPayouts(payoutsData || []);

        // Fetch rounds — the authoritative rotation order (susu_rounds).
        const { data: roundsData } = await supabaseClient
          .from('rounds')
          .select('*')
          .eq('group_id', parseInt(groupId, 10));
        setRounds(roundsData || []);
        setSusuOn(await checkFeatureFlag(FEATURE_FLAGS.SUSU_ROUNDS));

        // Log rotation view
        const { data: { user } } = await supabaseClient.auth.getUser();
        if (user) {
          await supabaseClient
            .from('audit_logs')
            .insert({
              event_category: 'rotation_management',
              event_type: 'rotation_viewed',
              actor_id: user.id,
              group_id: parseInt(groupId, 10),
              timestamp: new Date().toISOString()
            });
        }

      } catch (err) {
        console.error('Error fetching rotation data:', err);
        setError('Failed to load rotation data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupId]);

  const getMemberForHand = (hand) => {
    return membersData.find(m => m.id === hand.member_db_id);
  };

  // Authoritative rotation order comes from the rounds table (susu_rounds), via
  // the shared cycle-engine helpers — no position/payout-existence re-derivation.
  const currentRound = getLiveRound(rounds, parseInt(groupId, 10));
  const cycleComplete = isCycleComplete(rounds, parseInt(groupId, 10));
  // The cycle to check collections against: the live cycle, or (when complete) the
  // latest cycle — so a finished cycle resolves to COLLECTED, not WAITING.
  const effectiveCycle = currentRound?.cycle_number
    ?? (rounds.length ? Math.max(...rounds.map(r => Number(r.cycle_number))) : null);

  const collectedPayout = (hand) =>
    payouts.find(p => String(p.hand_id) === String(hand.id)
      && ['confirmed', 'advanced_unconfirmed'].includes(p.status));

  const getCollectionStatus = (hand) => {
    // COLLECTED = a confirmed/admin-advanced payout OR a confirmed round (direct).
    if (hasCollectedThisCycle(hand, effectiveCycle, payouts, rounds)) {
      return { status: 'COLLECTED', payout: collectedPayout(hand) };
    }
    // NEXT = the live round's collector.
    if (currentRound && String(currentRound.collector_hand_id) === String(hand.id)) {
      return { status: 'NEXT' };
    }
    return { status: 'WAITING' };
  };

  // Actual collection date for a collected hand (confirmed / admin-advanced / sent) —
  // shown instead of the future estimate once a position has collected.
  const getCollectedDate = (hand) => {
    // Payout date (Model B) or the round's confirmed_at (direct, no payout).
    const ts = collectedTimestamp(hand, effectiveCycle, payouts, rounds);
    return ts ? formatDate(new Date(ts)) : null;
  };

  // Collection date READS the stored scheduled_collection_date of the hand's
  // collector-round in the effective cycle (Stage 9). Flag-gated: susu_rounds OFF
  // is a complete kill switch — no leftover date text.
  const getEstimatedCollectionDate = (hand) =>
    (susuOn ? formatScheduleDate(getScheduledCollectionDate(hand, rounds, effectiveCycle)) : null);

  const handleEditRotation = async () => {
    // Check if any payout records exist for this group
    const { data: existingPayouts } = await supabaseClient
      .from('payouts')
      .select('id')
      .eq('group_id', parseInt(groupId, 10))
      .limit(1);

    if (existingPayouts && existingPayouts.length > 0) {
      const confirmed = window.confirm(
        "⚠️ Payouts have been sent for this cycle. " +
        "Changing rotation positions may affect payment " +
        "records. Please ensure all affected members agree " +
        "before making changes. Continue anyway?"
      );
      
      if (!confirmed) {
        return; // Stay in view mode
      }
    }

    setIsEditing(true);
    const positions = {};
    hands.forEach(hand => {
      positions[hand.id] = hand.position_number;
    });
    setEditingPositions(positions);
  };

  const handlePositionChange = (handId, newPosition) => {
    setEditingPositions({
      ...editingPositions,
      [handId]: newPosition
    });
  };

  const handleSaveRotation = async () => {
    try {
      // Check for duplicate position numbers
      const positions = Object.values(editingPositions).map(p => parseInt(p, 10));
      const uniquePositions = new Set(positions);
      
      if (uniquePositions.size !== positions.length) {
        alert('Each hand must have a unique position number. Please fix duplicate positions before saving.');
        return;
      }
      
      // Check for missing or invalid positions
      if (positions.some(p => !p || p < 1)) {
        alert('All hands must have a valid position number greater than 0.');
        return;
      }

      // Check if cycle has started
      const hasPayouts = payouts.length > 0;
      if (hasPayouts) {
        alert('Payouts have been sent. Contact members before changing rotation order.');
        return;
      }

      // Update hand positions
      const updates = Object.entries(editingPositions).map(([handId, position]) => ({
        id: handId,
        position_number: parseInt(position, 10)
      }));

      for (const update of updates) {
        await supabaseClient
          .from('hands')
          .update({ position_number: update.position_number })
          .eq('id', update.id);
      }

      // Log rotation update
      const { data: { user } } = await supabaseClient.auth.getUser();
      if (user) {
        await supabaseClient
          .from('audit_logs')
          .insert({
            event_category: 'rotation_management',
            event_type: 'rotation_updated',
            actor_id: user.id,
            group_id: parseInt(groupId, 10),
            new_value: {
              updated_positions: updates
            },
            timestamp: new Date().toISOString()
          });
      }

      setIsEditing(false);

      // Refetch updated rotation data
      const { data: refreshedHands } = await supabaseClient
        .from('hands')
        .select('*')
        .eq('group_id', parseInt(groupId, 10))
        .order('position_number', { ascending: true });
      setHands(refreshedHands || []);
    } catch (err) {
      console.error('Error saving rotation:', err);
      alert('Failed to save rotation changes');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingPositions({});
  };

  if (loading) return <div className="p-8">Loading rotation data...</div>;
  if (error) return <div className="p-8 text-error-600">{error}</div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-neutral-100 shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Rotation - {groupData?.group_name}
              </h1>
              <p className="text-sm text-neutral-600">
                {groupData?.contribution_frequency} cycle • {hands.filter(h => h.is_active !== false).length} hands
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!isEditing ? (
                <button
                  onClick={handleEditRotation}
                  className="px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm hover:bg-primary-700"
                >
                  Edit Rotation
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSaveRotation}
                    className="px-4 py-2 bg-success-600 text-white rounded-lg text-sm hover:bg-success-700"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700"
                  >
                    Cancel
                  </button>
                </>
              )}
              <button
                onClick={() => navigate(`/admin/group/${groupId}/payments`)}
                className="px-4 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700"
              >
                Back to Payments
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rotation Timeline */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-6">Rotation Timeline</h2>

            {cycleComplete && (
              <div className="mb-4 rounded-lg p-3 border border-success-500 bg-success-50">
                <p className="text-sm font-semibold text-success-800">
                  ✓ Cycle {effectiveCycle} complete — all members have collected
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              {hands
                .filter(h => h.is_active !== false)
                // Order by COLLECTION order (round_number), not assignment position.
                .sort((a, b) =>
                  (getCollectionPosition(a, rounds, effectiveCycle) ?? a.position_number)
                  - (getCollectionPosition(b, rounds, effectiveCycle) ?? b.position_number))
                .map((hand) => {
                  const collectionStatus = getCollectionStatus(hand);
                  const estimatedDate = getEstimatedCollectionDate(hand);
                  const collectionPos = getCollectionPosition(hand, rounds, effectiveCycle);
                  
                  return (
                    <div
                      key={hand.id}
                      className={`p-4 rounded-lg border-2 ${
                        collectionStatus.status === 'NEXT'
                          ? 'border-primary-500 bg-neutral-100'
                          : collectionStatus.status === 'COLLECTED'
                          ? 'border-success-500 bg-neutral-100'
                          : 'border-neutral-200 bg-neutral-100'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <div>
                              <h3 className="text-lg font-semibold text-neutral-900">
                                Position {collectionPos ?? hand.position_number}
                              </h3>
                              <p className="text-sm text-neutral-600">
                                {getMemberForHand(hand)?.name || 'Unassigned'}
                              </p>
                            </div>
                            
                            <div className="text-right">
                              <p className="text-sm text-neutral-500">
                                Contribution: {hand.contribution_amount != null ? formatCurrency(hand.contribution_amount) : 'Amount not set'}
                              </p>
                              {collectionStatus.payout && (
                                <p className="text-sm text-neutral-500">
                                  Payout: {collectionStatus.payout.payout_amount != null ? formatCurrency(collectionStatus.payout.payout_amount) : 'Amount not set'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="ml-4 text-right">
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            collectionStatus.status === 'NEXT'
                              ? 'bg-primary-100 text-primary-800'
                              : collectionStatus.status === 'COLLECTED'
                              ? 'bg-success-100 text-success-800'
                              : 'bg-warning-100 text-warning-800'
                          }`}>
                            {collectionStatus.status}
                          </div>
                          
                          {isEditing ? (
                            <input
                              type="number"
                              min="1"
                              value={editingPositions[hand.id] || hand.position_number}
                              onChange={e => handlePositionChange(hand.id, e.target.value)}
                              onClick={e => e.target.select()}
                              className="w-16 border border-neutral-300 rounded px-2 py-1 text-center"
                            />
                          ) : (
                            <div>
                              {collectionStatus.status === 'COLLECTED' ? (
                                <p className="text-sm text-success-700">
                                  Collected: {getCollectedDate(hand) || '—'}
                                </p>
                              ) : (
                                <p className="text-sm text-neutral-500">
                                  Collection Date: {estimatedDate || 'Not set'}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <ReportProblemButton />
        </div>
      </div>
    </div>
  );
};

export default RotationView;
